import { useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';

const JACKPOT_CYCLE_INTERVAL = 3000;
const JACKPOT_CYCLE_DURATION = 450;
const JACKPOT_TIER_COUNT = 3;

export type JackpotTier = { number: number; amount: string; color: string };
export type JackpotAmounts = { gold: string; silver: string; bronze: string };

/** Cycles the three backend-provided jackpot tiers as one vertically scrolling strip. */
export const useJackpotCycle = ({ gold, silver, bronze }: JackpotAmounts) => {
	const tiers: JackpotTier[] = [
		{ number: 1, amount: gold, color: '#F4AE2B' },
		{ number: 2, amount: silver, color: '#D6D9DF' },
		{ number: 3, amount: bronze, color: '#CD7F32' },
	];
	const offset = useRef(new Animated.Value(0)).current;
	const currentTierRef = useRef(0);
	const isAnimatingRef = useRef(false);
	const [currentTier, setCurrentTier] = useState(0);
	const [nextTier, setNextTier] = useState(1);
	const [pendingTier, setPendingTier] = useState<number | null>(null);

	useEffect(() => {
		const interval = setInterval(() => {
			if (isAnimatingRef.current) return;

			const next = (currentTierRef.current + 1) % JACKPOT_TIER_COUNT;
			isAnimatingRef.current = true;
			setNextTier(next);
			setPendingTier(next);
		}, JACKPOT_CYCLE_INTERVAL);

		return () => {
			clearInterval(interval);
			offset.stopAnimation();
		};
	}, [offset]);

	useEffect(() => {
		if (pendingTier === null) return;

		offset.setValue(0);
		const animation = Animated.timing(offset, {
			toValue: 1,
			duration: JACKPOT_CYCLE_DURATION,
			easing: Easing.out(Easing.cubic),
			useNativeDriver: true,
		});
		animation.start(({ finished }) => {
			isAnimatingRef.current = false;
			if (!finished) return;

			currentTierRef.current = pendingTier;
			setCurrentTier(pendingTier);
			setPendingTier(null);
		});

		return () => animation.stop();
	}, [offset, pendingTier]);

	return { current: tiers[currentTier], next: tiers[nextTier], offset };
};
